import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  fetchTrainingSimulatorHome,
  trainingHomeKey,
  type TrainingAttempt,
  type TrainingTranscriptGroup,
} from "@/lib/api/training-simulator/training-simulator";

const MIN_FINALIZING_DURATION_MS = 2500;

interface UseTrainingCompletionPresentationOptions {
  attemptId: string;
  attempt: TrainingAttempt | undefined;
  transcript: TrainingTranscriptGroup[] | undefined;
  completionDismissed: boolean;
}

export function useTrainingCompletionPresentation({
  attemptId,
  attempt,
  transcript,
  completionDismissed,
}: UseTrainingCompletionPresentationOptions) {
  const queryClient = useQueryClient();
  const [elapsedAttemptId, setElapsedAttemptId] = useState<string | null>(null);

  const isCompleted = attempt?.status === "completed";
  const isLevelCompletion =
    isCompleted === true && attempt?.completion_screen === "level";
  // `status` flips to "completed" a few seconds before the server resolves
  // which screen to show (module/simulator outcomes are finalized async). Treat
  // that gap as "still finalizing" so we don't flash the level-complete modal
  // before a module/simulator completion redirects to its full-page screen.
  const isAwaitingCompletionScreen =
    isCompleted === true && attempt?.completion_screen == null;
  const hasPostTargetTeenReply = hasTeenReplyAfterTargetMentorMessage(
    transcript,
    attempt?.level.completion_policy.target,
  );
  const shouldRunFinalizingDelay = hasPostTargetTeenReply || isLevelCompletion;
  const finalizingMinimumElapsed = elapsedAttemptId === attemptId;

  useEffect(() => {
    setElapsedAttemptId(null);
    if (!shouldRunFinalizingDelay) return;

    const timer = window.setTimeout(
      () => setElapsedAttemptId(attemptId),
      MIN_FINALIZING_DURATION_MS,
    );

    return () => window.clearTimeout(timer);
  }, [attemptId, shouldRunFinalizingDelay]);

  useEffect(() => {
    if (!hasPostTargetTeenReply && !isCompleted) return;

    // The session page already mounted this query on load. Refresh it once the
    // teen has replied so the modal can render the server-advanced next level.
    queryClient.prefetchQuery({
      queryKey: trainingHomeKey,
      queryFn: fetchTrainingSimulatorHome,
    });
  }, [hasPostTargetTeenReply, isCompleted, queryClient]);

  return {
    isCompleted,
    isFinalizing:
      isAwaitingCompletionScreen ||
      (shouldRunFinalizingDelay &&
        (attempt?.status === "in_progress" ||
          (isLevelCompletion && !finalizingMinimumElapsed))),
    showCompletion:
      isLevelCompletion && !completionDismissed && finalizingMinimumElapsed,
  };
}

function hasTeenReplyAfterTargetMentorMessage(
  transcript: TrainingTranscriptGroup[] | undefined,
  targetMentorMessageCount: number | undefined,
) {
  if (!transcript || !targetMentorMessageCount) return false;

  const timeline = transcript
    .flatMap((group) => group.messages)
    .map((message, index) => ({ message, index }))
    .sort((a, b) => {
      const byCreatedAt =
        new Date(a.message.created_at).getTime() -
        new Date(b.message.created_at).getTime();

      return byCreatedAt || a.index - b.index;
    });

  let mentorMessageCount = 0;

  for (let index = 0; index < timeline.length; index += 1) {
    const { message } = timeline[index];
    if (message.sender_type !== "mentor") continue;

    mentorMessageCount += 1;
    if (mentorMessageCount !== targetMentorMessageCount) continue;

    return timeline
      .slice(index + 1)
      .some((entry) => entry.message.sender_type === "simulated_teen");
  }

  return false;
}
